import type {MouseEvent} from 'react';

import {useThemeColors} from '@nfq/colors';
import {useFormContext} from '@nfq/react-form';

import type {FilterConfig} from '../FilterWrapper/FilterWrapper';
import type {FormValue} from '@nfq/react-form';

/**
 * The `useFilterTags` hook manages filter tag functionality and provides handlers for tag interactions.
 * This hook processes filter configurations to create displayable tags, handles tag removal logic,
 * and provides a reset mechanism for clearing all active filters.
 *
 * @param  filter Array of filter configurations containing filter definitions and options.
 * @returns Object containing colors, click handler, reset handler, and mapped tags.
 *
 * @throws {Error} Throws an error if used outside of a Form context.
 *
 * @example
 * ```tsx
 * const filterConfig = [
 *     {
 *         name: 'category',
 *         type: 'select',
 *         options: [{value: 'tech', label: 'Technology'}],
 *         onChange: (value) => console.log(value)
 *     }
 * ];
 *
 * const {mappedTags, handleClick, handleResetAll} = useFilterTags(filterConfig);
 * ```
 */
export const useFilterTags = (filter: FilterConfig[]) => {
    const colors = useThemeColors();
    const {form} = useFormContext() ?? {};

    if (!form) {
        throw new Error('useFilterTags must be used within a Form');
    }

    const mappedTags = filter.flatMap(item => {
        const value = form.values[item.name];

        if (Array.isArray(value)) {
            return value.map(v => ({
                ...item.tagConfig,
                label: item.tagConfig.label(item, item.options.find(option => option.value === v)),
                value: v as string
            }));
        }

        return [{
            ...item.tagConfig,
            label: item.tagConfig.label(item, item.options.find(option => option.value === value)),
            value: value as string
        }];
    });

    /**
     * Handles click events on filter tags to remove individual filter values.
     * This function extracts filter data from the clicked element and updates the form state
     * by removing the selected filter value while maintaining other active filters.
     *
     * @param e The mouse click event containing the filter tag data attributes.
     */
    const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
        const {name, value} = e.currentTarget.dataset;
        const item = filter.find(i => i.name === name);

        if (!item || !name || value === undefined) {
            return;
        }

        if (item.type === 'multi-select') {
            const currentValues: FormValue = Array.isArray(form.values[name]) ? form.values[name] : [];
            const newValues = currentValues.filter(v => v !== value) as string[];

            form.changeValue(
                name,
                newValues
            );
            item.onChange(newValues);
        } else {
            form.changeValue(name, '');
            item.onChange('');
        }
    };

    /**
     * Resets all filter values to their initial state and clears the form.
     * This function iterates through all configured filters and resets them to empty values,
     * with multi-select filters being reset to empty arrays and single-select filters to empty strings.
     */
    const handleResetAll = () => {
        form.resetValues();
        filter.forEach(item => {
            item.onChange(item.type === 'multi-select' ? [] : '');
        });
    };

    return {
        colors,
        handleClick,
        handleResetAll,
        mappedTags
    };
};